// client/src/components/campaign/ContentCalendar.jsx
//
// Month grid of social content, coloured by approval stage (On-Screen 2.0).
// Two entry points:
//   /:tenant/content-calendar             — everything scheduled this month
//   /:tenant/campaigns/:campaignId/calendar — one campaign's content
//
// A calendar answers "when does this go out"; the approval panel on each
// post answers "how does it get from idea to published".

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import { getCampaignContent } from '../../services/campaignsApi';
import { fetchUpcomingScheduledPosts } from '../../services/scheduledPostsApi';
import { APPROVAL_LABELS } from '../../services/contentApprovalApi';
import ContentApprovalPanel from './ContentApprovalPanel';

const STAGE_DOT = {
  idea: 'bg-slate-400',
  draft: 'bg-slate-400',
  in_review: 'bg-amber-500',
  changes_requested: 'bg-orange-500',
  approved: 'bg-emerald-500',
  rejected: 'bg-red-500',
  archived: 'bg-black/30',
  posted: 'bg-cobalt',
};

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startDay = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function ContentCalendar({ tenantId: tenantProp }) {
  const params = useParams();
  const tenantId = tenantProp || params.tenant;
  const campaignId = params.campaignId || null;
  const navigate = useNavigate();

  const [cursor, setCursor] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (campaignId) {
        const res = await getCampaignContent(tenantId, campaignId);
        setPosts((res.data || []).map((p) => ({
          ...p,
          when: p.scheduled_on || p.created_on,
          stage: p.status === 'posted' ? 'posted' : p.approval_status,
        })));
      } else {
        const res = await fetchUpcomingScheduledPosts(tenantId, { all: true, limit: 250 });
        setPosts((res.posts || res.data || []).map((p) => ({
          ...p,
          when: p.scheduled_on || p.scheduledOn || p.created_on,
          stage: p.status === 'posted' ? 'posted' : (p.approval_status || p.approvalStatus || 'draft'),
        })));
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  }, [tenantId, campaignId]);

  useEffect(() => { load(); }, [load]);

  const cells = useMemo(() => monthMatrix(cursor.y, cursor.m), [cursor]);
  const byDay = useMemo(() => {
    const map = {};
    for (const p of posts) {
      if (!p.when) continue;
      const key = String(p.when).slice(0, 10);
      (map[key] = map[key] || []).push(p);
    }
    return map;
  }, [posts]);

  const monthLabel = new Date(cursor.y, cursor.m, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const shift = (delta) => setCursor(({ y, m }) => {
    const d = new Date(y, m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  const footer = (
    <AppFooter buttons={[{ icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenantId}/dashboard`) }]} />
  );

  return (
    <div className="mx-auto max-w-4xl space-y-3 p-4" style={{ paddingBottom: 72 }}>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Content calendar</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="rounded border border-black/15 p-1.5 dark:border-white/15"><FaChevronLeft size={12} /></button>
          <span className="w-36 text-center text-sm font-medium">{monthLabel}</span>
          <button onClick={() => shift(1)} className="rounded border border-black/15 p-1.5 dark:border-white/15"><FaChevronRight size={12} /></button>
        </div>
      </div>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenantId}/dashboard`)}>
          <FaHome /> Home
        </button>
        <button type="button" onClick={() => shift(-1)}>
          <FaChevronLeft /> Prev
        </button>
        <span style={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>{monthLabel}</span>
        <button type="button" onClick={() => shift(1)}>
          Next <FaChevronRight />
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {loading && <div className="text-sm opacity-70">Loading…</div>}

      <div className="grid grid-cols-7 gap-px overflow-hidden rounded-lg border border-black/10 bg-black/10 text-xs dark:border-white/10 dark:bg-white/10">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="bg-white p-1 text-center font-semibold opacity-60 dark:bg-neutral-900">{d}</div>
        ))}
        {cells.map((date, i) => {
          const key = date ? date.toISOString().slice(0, 10) : `x${i}`;
          const dayPosts = date ? (byDay[key] || []) : [];
          return (
            <div key={key} className="min-h-[64px] bg-white p-1 dark:bg-neutral-900">
              {date && <div className="mb-0.5 text-right opacity-40">{date.getDate()}</div>}
              <div className="space-y-0.5">
                {dayPosts.slice(0, 4).map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setSelected(p)}
                    className="flex w-full items-center gap-1 truncate rounded bg-black/5 px-1 py-0.5 text-left hover:bg-black/10 dark:bg-white/10 dark:hover:bg-white/20"
                    title={`${APPROVAL_LABELS[p.stage] || p.stage}: ${p.content || ''}`}
                  >
                    <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${STAGE_DOT[p.stage] || 'bg-slate-400'}`} />
                    <span className="truncate">{p.content || '(media)'}</span>
                  </button>
                ))}
                {dayPosts.length > 4 && <div className="opacity-50">+{dayPosts.length - 4} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 text-xs opacity-70">
        {Object.entries({ draft: 'Draft/idea', in_review: 'In review', changes_requested: 'Changes', approved: 'Approved', posted: 'Published' }).map(([k, label]) => (
          <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${STAGE_DOT[k]}`} /> {label}</span>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full max-w-md space-y-3 rounded-xl bg-white p-5 text-black dark:bg-neutral-900 dark:text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs opacity-60">{selected.when ? new Date(selected.when).toLocaleString() : 'Unscheduled'}</div>
            <p className="text-sm whitespace-pre-wrap">{selected.content || '(media only)'}</p>
            <ContentApprovalPanel
              tenantId={tenantId}
              post={selected}
              onChanged={() => { setSelected(null); load(); }}
            />
            <div className="flex justify-end">
              <button onClick={() => setSelected(null)} className="rounded border border-black/15 px-3 py-1.5 text-sm dark:border-white/15">Close</button>
            </div>
          </div>
        </div>
      )}

      {footer}
    </div>
  );
}
